import type { ReactNode } from 'react'
import { useDrag } from 'react-dnd'

import { Box } from '@factorialco/deprecated-design-system'

import type {
  CardAppearance,
  TimeGridDragItem,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'

interface Props {
  gridColumn: string
  gridRow: string
  minHeight: number
  paddingX: number
  cardPaddingX: number
  cardPaddingY: number
  radius: number
  appearance?: CardAppearance
  dragItem?: TimeGridDragItem
  canDrag?: boolean

  eventId: string
  onEventSelected?: (
    eventId: string,
    modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
  ) => void
  children: ReactNode
}

const GridCard = ({
  gridColumn,
  gridRow,
  minHeight,
  paddingX,
  cardPaddingX,
  cardPaddingY,
  radius,
  appearance,
  dragItem,
  canDrag = false,
  eventId,
  onEventSelected,
  children,
}: Props) => {
  const [{ isDragging }, drag] = useDrag<TimeGridDragItem, void, { isDragging: boolean }>(
    () => ({
      type: dragItem?.type ?? '__time_grid_disabled__',
      item: dragItem ?? { type: '__time_grid_disabled__', payload: null },
      canDrag,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [dragItem, canDrag]
  )

  const setDragRef = canDrag ? drag : undefined

  // Card click: selects event and never falls through to cell selection.
  const handleCardClick = onEventSelected
    ? (event: MouseEvent) => {
        event.stopPropagation()
        onEventSelected(eventId, {
          shiftKey: event.shiftKey,
          metaKey: event.metaKey,
          ctrlKey: event.ctrlKey,
        })
      }
    : undefined

  return (
    <Box
      ref={setDragRef}
      style={{
        gridColumn,
        gridRow,
        alignSelf: appearance?.fillHeight ? 'stretch' : 'start',
        minHeight: `${String(minHeight)}px`,
        height: appearance?.fillHeight ? 'auto' : undefined,
        boxSizing: 'border-box',
        cursor: canDrag ? 'grab' : onEventSelected ? 'pointer' : undefined,
        opacity: isDragging ? '0.6' : undefined,
      }}
    >
      <Box
        background={appearance?.background}
        border={appearance?.border}
        className={appearance?.className}
        onClick={handleCardClick}
        style={{
          opacity: appearance?.opacity,
          margin: `0 ${String(paddingX)}px`,
          padding: `${String(cardPaddingY)}px ${String(cardPaddingX)}px`,
          borderRadius: `${String(radius)}px`,
          boxSizing: 'border-box',
          position: 'relative',
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

export { GridCard }
